"use client";

import React from "react";
import { useSearchParams } from "react-router-dom";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronLeft, ChevronRight } from "lucide-react";
function RelatedArticlesConsumer() {
  const {
    posts,
    loading,
    error,
    totalPages,
    currentPage,
    setPage
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="h-80 animate-pulse bg-muted/20 border-border" />)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground p-4 border border-destructive/20 rounded-lg bg-destructive/5">
        No se pudieron cargar las noticias.
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-20 border border-dashed border-border rounded-lg">
        <p className="text-muted-foreground font-sans">No se encontraron artículos en esta sección.</p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post, i) => <Card key={post.id} data-index={i} className="overflow-hidden border-border shadow-sm hover:shadow-lg transition-all duration-300 bg-card flex flex-col">
            {post.featuredImage && <div className="w-full h-48 overflow-hidden bg-muted shrink-0">
                <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
              </div>}
            <CardHeader className="p-6 pb-3 flex-grow-0">
              {post.categories && post.categories.length > 0 && <div className="mb-3">
                  <span className="inline-block px-3 py-1 text-xs font-semibold bg-primary/10 text-primary rounded-md uppercase tracking-wider">
                    {post.categories[0].name}
                  </span>
                </div>}
              <Link to={post.link || "#"} className="hover:text-primary transition-colors group">
                <CardTitle className="text-2xl font-default text-foreground leading-tight group-hover:text-primary">
                  {post.title}
                </CardTitle>
              </Link>
            </CardHeader>
            <CardContent className="p-6 pt-0 flex-grow">
              <p className="text-muted-foreground text-base line-clamp-3 font-sans">
                {post.excerpt?.replace(/(<([^>]+)>)/gi, "") || "Descubra más detalles sobre esta noticia y manténgase informado con nuestra cobertura exclusiva."}
              </p>
              <div className="mt-4">
                <Link to={post.link || "#"} className="text-primary font-semibold text-sm hover:underline decoration-primary underline-offset-4">
                  Leer Más →
                </Link>
              </div>
            </CardContent>
          </Card>)}
      </div>

      {totalPages > 1 && <div className="flex justify-center items-center gap-2 pt-8 border-t border-border">
          <button onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1} className="p-2 rounded-md border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors" aria-label="Página anterior">
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-1">
            {[...Array(totalPages)].map((_, i) => {
          const pageNum = i + 1;
          if (pageNum === 1 || pageNum === totalPages || pageNum >= currentPage - 1 && pageNum <= currentPage + 1) {
            return <button key={pageNum} onClick={() => setPage(pageNum)} className={`w-10 h-10 rounded-md text-sm font-medium transition-colors ${currentPage === pageNum ? "bg-primary text-primary-foreground" : "hover:bg-muted border border-transparent hover:border-border"}`}>
                    {pageNum}
                  </button>;
          }
          if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
            return <span key={pageNum} className="px-1 text-muted-foreground">...</span>;
          }
          return null;
        })}
          </div>

          <button onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages} className="p-2 rounded-md border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors" aria-label="Siguiente página">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>}
    </div>;
}
export default function RelatedArticlesGrid() {
  const [searchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "1", 10);
  const perPage = 12;
  const wp_query_params = {
    post_type: "post",
    post_status: "publish",
    per_page: perPage,
    page: page,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="257702" id="articlegrid" className="py-16 bg-background text-foreground">
      <div className="page-container">
        <div className="mb-12 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
          <div>
            <h2 className="text-4xl font-bold font-default text-foreground tracking-tight">
              Archivo de Noticias
            </h2>
            <p className="text-muted-foreground mt-2 font-sans max-w-2xl">
              Explore nuestra cobertura completa de los eventos más importantes en el suroeste de Florida.
            </p>
          </div>
          <Link to="/HomePage" className="text-primary font-bold hover:opacity-80 transition-opacity flex items-center gap-2">
            <ChevronLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
        </div>
        
        <PostsProvider wp_query={wp_query}>
          <RelatedArticlesConsumer />
        </PostsProvider>
      </div>
    </section>;
}